<template>
  <base-new-form>
    <template v-slot:header>
      {{ $t('renew_policy_info_header') }}
    </template>
    <div class="car-policy-info">
      <RenewInformation
        :productName="carProductName"
        :productType="productType"
        :policyNumber="policyNumber"
        :productTitle="productTitle"
        :endPolicyDate="endPolicyDate">
        <template v-slot:info>
          <li class="quote__info-list_bottom-count">
            <span>{{ $t('renew_car_info_seats') }}: {{ carInfo.seats }}</span>
            <span>{{ $t('renew_car_info_cylinders') }}: {{ carInfo.cylinders }}</span>
          </li>
          <li>{{ $t('renew_car_info_chassis') }}: {{ carInfo.chassis }}</li>
        </template>
      </RenewInformation>
      <RenewMotorInfo
        :title="productInfoName"
        :plateNumber="carInfo.plateNumber"
        :plateType="carInfo.type"
        :carShape="carInfo.shape"
        :carShapes="carShapes"
        :plateTypes="filtredPlateTypes"
        :disablePlateNumber="!isPlateNumberValidOnInitialization"
        :disablePlateType="!isPlateTypeValidOnInitialization"
        always-display
        editable
        @edit="onEditMotorInfo"/>
      <PlateNumberAndTypeInfo
          v-if="!isPlateNumberValidOnInitialization || !isPlateTypeValidOnInitialization"
          :showPlateNumber="!isPlateNumberValidOnInitialization"
          :showPlateType="!isPlateTypeValidOnInitialization"
          :plateTypes="filtredPlateTypes"
          @editPlateNumber="onEditPlateNumber"
          @editPlateType="onEditPlateType"
          ref="providePlateNumber"
      />
      <QuotePolicyOwner
        :fullName="fullName"
        :qid="qid"
        always-display
        editable
        @openEditOwner="openEditOwner"
        @blurName="trackOwnerName"
        @blurQid="trackOwnerQid"
        @edit="onEditUserInfo">
      </QuotePolicyOwner>

      <UserContactInfo
        :email="policyInfoEmail"
        :phone="policyInfoPhone"
        :dob="policyInfoDOB"
        ref="contacts"/>

      <PromoInputWithApply
          v-model="promocode"
          @blur="trackPromocodeChange"
          @focus="trackInputPromocode"
          @clickPromocode="trackPromocodeClick"
          :validation="promocodeValidation.bind(this)"/>
    </div>
      <base-button @click.prevent="onSubmit"></base-button>
    </base-new-form>
</template>

<script>
import { BaseNewForm, PromoInputWithApply, QuotePolicyOwner, RenewInformation, wizardStepMixin } from 'qic-components'
import RenewMotorInfo from '@/components/RenewMotorInfo.vue'
import UserContactInfo from '@/components/renewMotorWizardPages/MotorPolicyInfo/UserContactInfo.vue'
import { trackEvent, trackGtag } from '@/utils/useGtag'
import PlateNumberAndTypeInfo from '@/components/renewMotorWizardPages/MotorPolicyInfo/PlateNumberAndTypeInfo.vue'
import { mapState } from 'vuex'
import ErrorTooManyRequests from '@/shared/errors/ErrorTooManyRequests'
import analyticsDataMixin from '@/mixins/analyticsDataMixin'
import { INITIAL_TRANS_ID_STORE_NAME } from '@/constants/constants.ts'
import { setStorage } from '@/utils/localstorage'
import { ERROR_CODES } from '@/shared/errors/errorCodes'
import { POLICY_CASES } from '@/constants/policyConstants'
import { getErrorMessage } from '@/shared/errors/getErrorMessage'

export default {
  name: 'CarPolicyInfo',
  mixins: [
    wizardStepMixin,
    analyticsDataMixin
  ],
  components: {
    RenewInformation,
    BaseNewForm,
    QuotePolicyOwner,
    RenewMotorInfo,
    PromoInputWithApply,
    UserContactInfo,
    PlateNumberAndTypeInfo
  },
  data () {
    return {
      editInfo: false,
      plateTypes: null,
      carShapes: null,
      promocode: this.$store.state.motor.promocode,
      isPlateNumberValidOnInitialization: true,
      isPlateTypeValidOnInitialization: true,
      qid: this.$store.state.renew.userData.qid,
      fullName: null
    }
  },
  computed: {
    ...mapState({
      policyInfo: state => state.renew.policyInfo
    }),
    policyNumber () {
      if (!this.policyInfo) return ''
      return this.policyInfo?.policyNo || ''
    },
    carInfo () {
      if (!this.policyInfo) return {}
      const vehicleCode = this.policyInfo.vehicleShape
      const typeCode = this.policyInfo.plateCharacter
      const curShape = this.carShapes && this.carShapes.find(s => s.code === vehicleCode)
      const curType = this.plateTypes && this.plateTypes.find(s => s.code === typeCode)

      return {
        makeModel: this.policyInfo.makeModel,
        modelYear: this.policyInfo.modelYear,
        policyNumber: this.policyInfo.policyNo,
        chassis: this.policyInfo.chassisNo,
        cylinders: this.policyInfo.noOfCylinder,
        seats: this.policyInfo.seatingCapacity,
        shape: {
          id: vehicleCode,
          name: this.locale === 'ar' ? curShape?.descAr : curShape?.desc
        },
        type: {
          id: typeCode,
          name: this.locale === 'ar' ? curType?.descAr : curType?.desc
        },
        plateNumber: this.policyInfo.regNo
      }
    },
    productType () {
      return this.$store.state.renew.productType
    },
    productInfoName () {
      return this.$store.getters.productInfoName
    },
    policyInfoEmail () {
      return this.policyInfo.emailId
    },
    policyInfoPhone () {
      return this.policyInfo.mobileNo
    },
    policyInfoDOB () {
      return this.policyInfo.driverDOB
    },
    productTitle () {
      return `${this.carInfo.makeModel} ${this.carInfo.modelYear}`
    },
    endPolicyDate () {
      if (!this.policyInfo) return new Date()
      return this.policyInfo.polEndDate && new Date(this.policyInfo.polEndDate)
    },
    carProductName () {
      if (!this.policyInfo) return ''
      if (this.productType === 'moto') return this.$t('renew_moto_insurance')
      return this.policyInfo.prodCode === '0110' ? this.$t('renew_car_comprehensive') : this.$t('renew_car_tpl')
    },
    locale () {
      return this.$i18n.locale
    },
    filtredPlateTypes () {
      if (!this.plateTypes) return []
      return this.plateTypes.filter(s => {
        return Number(s.value) === 1
      })
    },
    trackedPromocode: {
      get () {
        return this.promocode
      },
      set (value) {
        this.promocode = value
        this.trackPromocodeChange(value)
      }
    }
  },
  methods: {
    openEditOwner () {
      trackEvent('openEditOwner')
    },
    trackPromocodeClick (show) {
      const label = show ? 'on' : 'off'
      trackEvent('choosePromotion', label)
    },
    trackPromocodeChange (value) {
      trackEvent('typePromocode', value)
    },
    trackInputPromocode () {
      trackEvent('typePromocodeStart')
    },
    trackOwnerName (data) {
      const { numberOfSymbols } = data
      trackEvent('typeFullName', numberOfSymbols.toString())
    },
    trackOwnerQid (data) {
      const { numberOfSymbols } = data
      trackEvent('typeQatarID', numberOfSymbols.toString())
    },
    validatePlateNumber (plateNumber) {
      const regex = /^\d{1,6}$/
      return regex.test(plateNumber)
    },
    plateNumberIsValid () {
      if (!this.isPlateNumberValidOnInitialization) {
        return this.$refs.providePlateNumber.isValid()
      }
      return this.validatePlateNumber(this.carInfo.plateNumber)
    },
    onSubmit () {
      const contacts = this.$refs.contacts.validateAndGetValues()

      if (!contacts || !this.plateNumberIsValid()) return

      this.$store.commit('setUserPhone', contacts.phone.replaceAll(' ', '').replaceAll('+', ''))
      this.$store.commit('setUserEmail', contacts.email)

      this.$store.commit('setMotorPromocode', this.promocode)

      /** https://qicbizdev.atlassian.net/browse/QIC-2615 Turn off upgrade flow (for investigation) */
      // this.$store.commit('setMotorDob', contacts.dob)
      //
      // this.$store.dispatch('setMotorDob', {
      //   dateOfBirth: contacts.dob,
      //   qid: this.userInfo.qid
      // })

      const params = this.getAnalyticsData()

      trackGtag('click_proceed', {
        page: 'policy_information',
        policy_id: this.policyInfo.policyNo,
        ...params
      })

      this.loadNextPage(
        this.$store.dispatch('getMotorRenewPolicy')
          .then(() => {
            this.$emit('next')
          })
          .catch(error => {
            if (error instanceof ErrorTooManyRequests) {
              this.$emit('error', error)
              return
            }

            const errorData = error.data

            const errorCodes = ['1002', POLICY_CASES.PAYMENT_LINK]
            if (typeof error === 'object' && errorCodes.includes(errorData.errorCode)) {
              if (error.errorMessage === POLICY_CASES.PAYMENT_LINK || errorData.errorCode === ERROR_CODES.ACTIVE_PAYMENT_LINK_AVAILABLE) {
                return this.$router.replace({ name: 'PolicyNotFound', params: { policyCase: POLICY_CASES.PAYMENT_LINK, payLink: errorData.payLink } })
              } else {
                return this.$router.replace({ name: 'PolicyNotFound', params: { policyCase: errorData.errorCode } })
              }
            }

            throw new Error(getErrorMessage(error) ?? 'error not found')
          })
      )
    },

    onEditUserInfo (userInfo) {
      this.qid = userInfo.qid
      this.fullName = userInfo.fullName
      this.$store.commit('setUserQid', this.qid)
      this.$store.commit('setFullName', this.fullName)
      this.$store.commit('setUserPhone', typeof userInfo.phone === 'string' ? userInfo.phone.replace('+', '') : userInfo.phone)
    },
    onEditPlateNumber (plateNumber) {
      this.$store.commit('setPlateNumber', plateNumber)
    },
    onEditPlateType (plateType) {
      this.$store.commit('setPlateCharacter', plateType)
    },
    onEditMotorInfo (motorInfo) {
      this.$store.commit('setVehicleShape', motorInfo.shape.id)
      this.$store.commit('setPlateCharacter', motorInfo.type.id)
      this.$store.commit('setPlateNumber', motorInfo.plateNumber)
    },
    promocodeValidation (promocode) {
      return this.$store.dispatch('isPromocdoeValid', { promocode, type: 'OD' })
    },
    getInfoFromStorage () {
      this.$store.dispatch('setUserLoginData')
    }
  },
  created () {
    setStorage({ key: INITIAL_TRANS_ID_STORE_NAME, value: null })
    this.getInfoFromStorage()
  },
  mounted () {
    this.load(
      Promise.all([
        this.$store.dispatch('getMotorShapesDependOnVehicle', this.policyInfo?.vehicleTypeCode),
        this.$store.dispatch('getMotorPlateTypesWithParams', {
          vehicleType: this.policyInfo?.vehicleTypeCode,
          vehicleUsage: this.policyInfo?.vehicleUsage
        })
      ])
        .then(([carShapes, plateTypes]) => {
          this.fullName = this.$store.state.renew.policyInfo.insName
          this.qid = this.$store.state.renew.userData.qid

          this.carShapes = carShapes
          if (this.carShapes.length > 0) {
            const carShape = this.carShapes.find(s => s.code === this.policyInfo.vehicleShape)
            if (!carShape) {
              this.$store.commit('setVehicleShape', this.carShapes[0].code)
            }
          }

          this.plateTypes = plateTypes
          if (this.plateTypes.length === 1 && !this.policyInfo.plateCharacter) {
            this.$store.commit('setPlateCharacter', this.plateTypes[0].code)
          }
        })
        .finally(() => {
          this.isPlateNumberValidOnInitialization = this.validatePlateNumber(this.carInfo.plateNumber)
          this.isPlateTypeValidOnInitialization = this.policyInfo.plateCharacter
        })
    )
  }
}
</script>
